import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DsAlert } from "../../../design-system";
import { usePermissions } from "../../../rbac/PermissionsContext";
import { ADMIN_SHELL_NAV_EVENT } from "../../../utils/adminShellNav";
import HiringPortalProvider from "./HiringPortalProvider";
import PortalDashboardPanel from "./panels/PortalDashboardPanel";
import PortalJobsPanel from "./panels/PortalJobsPanel";
import PortalCandidatesPanel from "./panels/PortalCandidatesPanel";
import PortalAtsPanel from "./panels/PortalAtsPanel";
import PortalReferralsPanel from "./panels/PortalReferralsPanel";
import PortalSettingsPanel from "./panels/PortalSettingsPanel";

/** Tabs driven by admin Hiring icon children (?tab=) — same pattern as Employee. */
const TAB_SCREENS = {
  dashboard: "dashboard",
  jobs: "mrf_jobs",
  candidates: "candidates",
  ats: "ats",
  referrals: "referrals",
  settings: "hiring_settings",
};

const VALID_TABS = Object.keys(TAB_SCREENS);

function resolveTab(raw) {
  const tab = String(raw || "dashboard").toLowerCase();
  if (tab === "mrf_jobs" || tab === "mrf" || tab === "offers") return "jobs";
  if (tab === "hiring_settings") return "settings";
  return VALID_TABS.includes(tab) ? tab : "dashboard";
}

export default function HiringModule() {
  const { hasModuleAccess, hasScreenAccess, isAdmin } = usePermissions();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() =>
    resolveTab(searchParams.get("tab"))
  );
  const [settingsSub, setSettingsSub] = useState(
    () => searchParams.get("sub") || "policies"
  );

  const canViewModule =
    isAdmin ||
    hasModuleAccess("hiring", "view") ||
    Object.values(TAB_SCREENS).some((screen) =>
      hasScreenAccess("hiring", screen, "view")
    );

  const canViewTab = useCallback(
    (tabId) => {
      if (isAdmin) return true;
      const screen = TAB_SCREENS[tabId];
      if (!screen) return false;
      return (
        hasScreenAccess("hiring", screen, "view") ||
        hasModuleAccess("hiring", "view")
      );
    },
    [hasModuleAccess, hasScreenAccess, isAdmin]
  );

  const applyTab = useCallback(
    (tabId, sub) => {
      const next = resolveTab(tabId);
      if (!canViewTab(next) && next !== "settings") {
        const fallback = VALID_TABS.find((t) => t !== "settings" && canViewTab(t));
        if (fallback) setActiveTab(fallback);
        return;
      }
      setActiveTab(next);
      if (sub) setSettingsSub(sub);
    },
    [canViewTab]
  );

  useEffect(() => {
    applyTab(searchParams.get("tab"), searchParams.get("sub"));
  }, [searchParams, applyTab]);

  useEffect(() => {
    const onShellNav = (event) => {
      if (event?.detail?.module !== "hiring") return;
      applyTab(event.detail.tab, event.detail.sub);
    };
    window.addEventListener(ADMIN_SHELL_NAV_EVENT, onShellNav);
    return () => window.removeEventListener(ADMIN_SHELL_NAV_EVENT, onShellNav);
  }, [applyTab]);

  useEffect(() => {
    if (!canViewTab(activeTab) && activeTab !== "settings") {
      const fallback = VALID_TABS.find((t) => t !== "settings" && canViewTab(t));
      if (fallback) setActiveTab(fallback);
    }
  }, [activeTab, canViewTab]);

  const canCreateCandidate =
    isAdmin ||
    hasModuleAccess("hiring", "create") ||
    hasScreenAccess("hiring", "candidates", "create");
  const canCreateAts =
    isAdmin ||
    hasModuleAccess("hiring", "create") ||
    hasScreenAccess("hiring", "ats", "create");
  const canEditAts =
    isAdmin ||
    hasModuleAccess("hiring", "edit") ||
    hasScreenAccess("hiring", "ats", "edit") ||
    hasScreenAccess("hiring", "ats", "approve");
  const canEditSettings =
    isAdmin ||
    hasModuleAccess("hiring", "edit") ||
    hasScreenAccess("hiring", "hiring_settings", "edit");

  const content = useMemo(() => {
    if (activeTab === "dashboard") return <PortalDashboardPanel />;
    // Jobs = functional portal Jobs UI (not MRF). No separate MRF tab.
    if (activeTab === "jobs") return <PortalJobsPanel />;
    if (activeTab === "candidates") {
      return <PortalCandidatesPanel canCreate={canCreateCandidate} />;
    }
    if (activeTab === "ats") {
      return <PortalAtsPanel canCreate={canCreateAts} canEdit={canEditAts} />;
    }
    if (activeTab === "referrals") return <PortalReferralsPanel />;
    if (activeTab === "settings" && canViewTab("settings")) {
      return (
        <PortalSettingsPanel canEdit={canEditSettings} initialSub={settingsSub} />
      );
    }
    return null;
  }, [
    activeTab,
    canCreateAts,
    canCreateCandidate,
    canEditAts,
    canEditSettings,
    canViewTab,
    settingsSub,
  ]);

  if (!canViewModule) {
    return (
      <div className="ds-page">
        <div className="ds-page-shell flex items-center justify-center p-6">
          <div className="hiring-theme max-w-md rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-6 text-center">
            <h4 className="ds-h4 mb-1">No hiring access</h4>
            <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
              You do not have permission to view Hiring. Contact your administrator.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <HiringPortalProvider>
      <div className="ds-page hiring-theme">
        <div className="ds-page-shell">
          <div className="ds-page-body">
            <div className="ds-page-main">
              <div className="ds-page-content-flush flex min-h-0 flex-1 flex-col overflow-hidden text-[hsl(var(--ds-fg))]">
                {!content && (
                  <div className="ds-page-alert m-4">
                    <DsAlert tone="warning">
                      No Hiring screens are enabled for your role.
                    </DsAlert>
                  </div>
                )}
                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4 lg:p-6">
                  {content}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HiringPortalProvider>
  );
}
